import React from 'react';
import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { SvgSpacesMediumDsEmbedded } from '../../../../../gen-src/icons';
import {
  DatabaseMediumIcon,
  getPaddingTopStyleForTextWithIcon,
  MetadataTable,
  MultiLineLinkWithIcon,
  SiteUrlsContext,
  ViewerPaneObfuscatedPlaceholder,
} from '../../../data-catalog-shared';
import { LabelWrapper } from '../Labels/LabelsHookHelper';
import { PublishedDatasourceIcon, PublishedDatasourceIconTooltip } from './PublishedDatasourceIcon';
import { LabelIconWrapper, DatasourceIconNameContainer, DatasourceListBodyElement } from './ViewerPaneStyles';
import { VizClientDatabasesConnectionNode, VizClientDatasource, VizClientPublishedDatasource } from './VizClientInterfaces';

const lastRefreshedLabelMinWidth = 87; // hack to prevent from wrapping to new line when scrollbars appear

export interface VizClientDatasourceElementProps {
  readonly datasource: VizClientDatasource;
  readonly workbookVizportalUrlId: string; // For generating links to workbook lineage details
}

/** A widget to display a single datasource */
export class VizClientDatasourceElement extends React.PureComponent<VizClientDatasourceElementProps> {
  public override render(): JSX.Element {
    return (
      <SiteUrlsContext.Consumer>
        {urls =>
          <DatasourceListBodyElement data-tb-test-id={'vizclient-datasource-element-test-id'}>
            <DatasourceIconNameContainer>
              <LabelWrapper itemInfo={this.props.datasource} category={'warning'} isEager={false} kind={'icon'} popoverPlacement={'left'} wrapper={(e) => <LabelIconWrapper>{e}</LabelIconWrapper>} />
              <MultiLineLinkWithIcon
                linkText={this.props.datasource.name}
                linkHref={urls.workbookLineageFieldsUrl(this.props.workbookVizportalUrlId, this.props.datasource.id)}
                iconComponent={<DatasourceIconElement datasource={this.props.datasource} />}
                iconPosition='top'
                iconSize='medium'
              />
            </DatasourceIconNameContainer>
            <DatasourceConnections datasource={this.props.datasource} />
            {
              // Only display the database connections if the embedded datasource is connected to
              // a local file/table (i.e., not to a published datasource)
              !this.props.datasource.hasPublishedDatasource
              && <DatasourceDatabasesConnections
                connections={this.props.datasource.databasesConnectionNodes}
                datasource={this.props.datasource}
              />
            }
          </DatasourceListBodyElement>
        }
      </SiteUrlsContext.Consumer>
    );
  }
}


function DatasourceIconElement(props: { datasource: VizClientDatasource }): JSX.Element | null {
  return props.datasource.__typename === 'EmbeddedDatasource'
    ? <SvgSpacesMediumDsEmbedded />
    : null; // No icon for published data sources
}

function DatasourceConnections(props: { datasource: VizClientDatasource }): JSX.Element | null {
  if (!props.datasource.hasPublishedDatasource) {
    return null;
  }

  return (
    <MetadataTable.Table valueCellFlexGrow={2}>
      <PublishedDatasourceConnectionIconNameRow datasource={props.datasource.publishedDatasource} />
      <PublishedDatasourceExtractLiveRow datasource={props.datasource} publishedDatasource={props.datasource.publishedDatasource} />
    </MetadataTable.Table>
  );
}

function DatasourceDatabasesConnections(props:
  {
    datasource: VizClientDatasource
    connections: ReadonlyArray<VizClientDatabasesConnectionNode> | null
  }): JSX.Element | null {

  if (!props.connections) {
    return null;
  }

  return (
    <MetadataTable.Table
      valueCellFlexGrow={2}
      data-tb-test-id={'datasource-databases-connections-test-id'}
    >
      {props.connections.map(connection =>
        <React.Fragment key={connection.id}>
          <EmbeddedDatasourceConnectionIconNameRow connection={connection} />
          <EmbeddedDatasourceExtractLiveRow connection={connection} datasource={props.datasource} />
        </React.Fragment>
      )}
    </MetadataTable.Table>
  );
}

function EmbeddedDatasourceConnectionIconNameRow(props: { connection: VizClientDatabasesConnectionNode | null }): JSX.Element | null {
  const isObfuscated = props.connection === null || props.connection.name === null;
  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <MetadataTable.Row>
          <MetadataTable.LabelCell style={getPaddingTopStyleForTextWithIcon({ iconSize: 'medium' })}>
            {Messages.DatasourceConnectionLabel()}
          </MetadataTable.LabelCell>
          <MetadataTable.ValueCell>
            {isObfuscated ? ViewerPaneObfuscatedPlaceholder() :
              <MultiLineLinkWithIcon
                iconComponent={props.connection ? DatabaseMediumIcon({
                  database: {
                    __typename: props.connection.__typename,
                    connectionType: props.connection.connectionType,
                    isCertified: props.connection.isCertified || false,
                  }
                }) : <div />}
                iconSize='medium'
                iconPosition='top'
                linkText={props.connection && props.connection.name || ''}
                linkHref={props.connection && urls.catalogDatabaseUrl(props.connection.id) || ''}
              />
            }
          </MetadataTable.ValueCell>
        </MetadataTable.Row>
      }
    </SiteUrlsContext.Consumer>
  );
}

function EmbeddedDatasourceExtractLiveRow(props:
  { datasource: VizClientDatasource, connection: VizClientDatabasesConnectionNode | null }): JSX.Element | null {

  return (
    <MetadataTable.Row>
      <MetadataTable.LabelCell style={{ minWidth: lastRefreshedLabelMinWidth }}>
        {Messages.DatasourceExtractLiveLabel()}
      </MetadataTable.LabelCell>
      <MetadataTable.ValueCell>
        {props.datasource.hasExtracts
          ? (
            props.datasource.extractLastUpdateTime
              ? Formatters.formatMediumDateTimeWithoutSeconds(new Date(props.datasource.extractLastUpdateTime))
              : Messages.DatasourceExtractValue()
          )
          : Messages.DatasourceLiveValue()
        }
      </MetadataTable.ValueCell>
    </MetadataTable.Row>
  );
}

function PublishedDatasourceConnectionIconNameRow(props: { datasource: VizClientPublishedDatasource | null }): JSX.Element | null {
  const isObfuscated = props.datasource === null || props.datasource.name === null;
  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <MetadataTable.Row>
          <MetadataTable.LabelCell style={getPaddingTopStyleForTextWithIcon({ iconSize: 'medium' })}>
            {Messages.DatasourceConnectionLabel()}
          </MetadataTable.LabelCell>
          <MetadataTable.ValueCell>
            {isObfuscated ? ViewerPaneObfuscatedPlaceholder() :
              <MultiLineLinkWithIcon
                iconComponent={props.datasource &&
                  PublishedDatasourceIcon(
                    { hasExtracts: !!props.datasource.hasExtracts, isCertified: props.datasource.isCertified }
                  )}
                iconSize='medium'
                iconPosition='top'
                iconTitle={props.datasource &&
                  PublishedDatasourceIconTooltip(
                    { hasExtracts: !!props.datasource.hasExtracts, isCertified: props.datasource.isCertified }
                  )}
                linkText={props.datasource && props.datasource.name || ''}
                linkHref={urls.datasourceLineageFieldsUrl(props.datasource && props.datasource.vizportalUrlId || '')}
              />
            }
          </MetadataTable.ValueCell>
        </MetadataTable.Row>
      }
    </SiteUrlsContext.Consumer>
  );
}

function PublishedDatasourceExtractLiveRow(props: {
  datasource: VizClientDatasource,
  publishedDatasource: VizClientPublishedDatasource | null
}): JSX.Element | null {
  const isObfuscated = props.publishedDatasource === null || props.publishedDatasource.name === null;
  if (!props.datasource.hasExtracts && isObfuscated) {
    return null;
  }

  const hasExtracts = props.datasource.hasExtracts || (props.publishedDatasource && props.publishedDatasource.hasExtracts);
  let extractText;

  if (hasExtracts) {
    if (props.datasource.extractLastUpdateTime) {
      extractText = Formatters.formatMediumDateTimeWithoutSeconds(new Date(props.datasource.extractLastUpdateTime));
    } else if (props.publishedDatasource && props.publishedDatasource.extractLastUpdateTime) {
      extractText = Formatters.formatMediumDateTimeWithoutSeconds(new Date(props.publishedDatasource.extractLastUpdateTime));
    } else {
      extractText = Messages.DatasourceExtractValue();
    }
  } else {
    extractText = Messages.DatasourceLiveValue();
  }

  return (
    <MetadataTable.Row>
      <MetadataTable.LabelCell style={{ minWidth: lastRefreshedLabelMinWidth }}>
        {Messages.DatasourceExtractLiveLabel()}
      </MetadataTable.LabelCell>
      <MetadataTable.ValueCell>
        {extractText}
      </MetadataTable.ValueCell>
    </MetadataTable.Row>
  );
}
